/**
 * Shared source of truth for the session / patient items the Projects
 * composers can link as context.
 *
 * Both the explicit ``LinkContextButton`` picker and the inline ``@``
 * mention autocomplete need the same lists — recent sessions plus the
 * patient profiles those sessions are linked to. A session can carry a
 * free-text patient name with no profile; that name is not a patient
 * and must not appear in the patient picker (the Patients page would
 * not list it, and linking it sends the agent a name it cannot resolve).
 * Keeping the derivation here means the two surfaces can never drift
 * (e.g. the profile-by-name matching) and the filtering for the ``@``
 * query lives in one place.
 */

import type { Shared__enums__enums__Gender } from '@heidi/schema/ml-scribe';
import { useMemo } from 'react';
import { useIntl, type IntlShape } from 'react-intl';

import { sharedAgentMessages } from '../../shared.messages';

import type { LinkedContextItem } from '../../contracts/projects/linked-context';
import type { Artifact } from './artifacts/state';
import {
  hasPatientNameFields,
  resolvePatientAvatarSeed,
  resolvePatientListName,
  type PatientNameFields,
} from './patientListName';
import {
  objectDayLabel,
  sessionDurationLabel,
  sessionTokenIdentity,
  sessionTokenLabel,
} from './session-token-label';

/** Default cap on rows surfaced per section — a glance surface. */
const DEFAULT_LIMIT = 5;

export interface LinkableContextItems {
  /** Recent sessions (filtered by ``query`` when supplied), capped. */
  sessionItems: LinkedContextItem[];
  /** Recent unique patients (filtered by ``query``), capped. */
  patientItems: LinkedContextItem[];
}

/**
 * The session-list fields this derivation reads, and NOTHING else.
 *
 * STRUCTURAL RATHER THAN THE UPSTREAM RESPONSE TYPE, and that is the point of
 * writing it out: the rows arrive from a hook that stays app-side, and naming the
 * seven fields the sort and the patient join actually touch is what makes the
 * derivation testable without a session fixture — and what makes it obvious, at
 * review, that this hook reads no PHI beyond the name it renders.
 */
export interface LinkableSessionRow {
  readonly session_id: string;
  readonly session_name?: string | null;
  // Both nullable, matching the wire: the parse falls back to "now" when neither
  // is set, which is what the pre-move sort did and what keeps a half-written row
  // from throwing inside a comparator.
  readonly created_at?: string | null;
  readonly created_time_local?: string | null;
  /** Presence flips the timestamp parse to account-local. */
  readonly ehr_appt_id?: string | null;
  readonly duration?: number | null;
  readonly patient?: { readonly name?: string | null } | null;
  readonly patient_profile?: (PatientNameFields & { readonly id?: string | null }) | null;
}

/** A clinic-side profile row, read for the real `patientId` and its name fields. */
export interface LinkablePatientProfile extends PatientNameFields {
  readonly id?: string | null;
  readonly gender?: Shared__enums__enums__Gender | null;
  readonly birth_date?: string | null;
  readonly last_session_created_at?: string | null;
}

type SessionContextItem = Extract<LinkedContextItem, { type: 'session' }>;

/**
 * Account-timezone-aware timestamp parse, injected.
 *
 * A PORT AND NOT A MOVE: the app's `parseTimeStamp` reads the signed-in account's
 * timezone, which is app state by recorded decision, so the parse cannot come
 * behind a package door. The sort ORDER is this module's decision and stays here;
 * only the clock crosses.
 */
export type ParseLinkableTimestamp = (
  time: string | null | undefined,
  isUtc: boolean
) => Date;

/**
 * The three app-resolved inputs, bundled so a component threading them reads as
 * one decision rather than three loose props.
 */
export interface LinkableContextSources {
  /** Recent sessions, in any order — this hook sorts newest-first. */
  readonly sessions: readonly LinkableSessionRow[];
  readonly profiles: readonly LinkablePatientProfile[] | undefined;
  readonly parseTimestamp: ParseLinkableTimestamp;
}

export interface UseLinkableContextItemsArgs extends LinkableContextSources {
  /** When set, filters sessions (name / patient) and patients (name). */
  query?: string;
  /** Rows surfaced per section. Defaults to {@link DEFAULT_LIMIT}. */
  limit?: number;
  // A port: sex and age read the patients domain's identity formatting, which is app-side.
  patientSexAge?: (profile: LinkablePatientProfile) => string | undefined;
}

const toSessionContextItem = (
  session: LinkableSessionRow,
  profile: PatientNameFields | undefined,
  {
    sessionName,
    patientName,
    intl,
    parseTimestamp,
  }: {
    sessionName: string;
    patientName: string | undefined;
    intl: IntlShape;
    parseTimestamp: ParseLinkableTimestamp;
  }
): SessionContextItem => {
  const identity = sessionTokenIdentity(
    { ...session, patient_profile: profile },
    intl,
    parseTimestamp
  );
  const duration = sessionDurationLabel(session.duration);
  return {
    type: 'session',
    sessionId: session.session_id,
    sessionName,
    patientName,
    ...(patientName
      ? { avatarSeed: resolvePatientAvatarSeed(profile, session.patient?.name) }
      : {}),
    createdAt: parseTimestamp(
      session.created_time_local ?? session.created_at,
      !session.ehr_appt_id
    ).getTime(),
    tokenLabel: sessionTokenLabel(identity),
    ...(identity.title ? { sessionTitle: identity.title } : {}),
    dateLabel: identity.dayTime,
    ...(duration ? { duration } : {}),
  };
};

/**
 * Map a linked item to the canvas artifact it should open: a session
 * opens its session view; a patient opens the real patient artifact
 * when a profile id resolved. A patient with no resolvable profile id
 * opens nothing (``null``) rather than silently surfacing one of their
 * sessions — opening a session in place of the patient record is the
 * exact mis-routing this avoids.
 */
export const artifactForLinkedItem = (item: LinkedContextItem): Artifact | null => {
  if (item.type === 'session') {
    return { type: 'session', sessionId: item.sessionId };
  }
  if (item.type === 'library_document') {
    return { type: 'library-document', documentId: item.documentId };
  }
  if (item.type === 'patient' && item.patientId) {
    return { type: 'patient', patientId: item.patientId };
  }
  if (item.type === 'task' && item.sessionId) {
    return { type: 'tasks', sessionId: item.sessionId };
  }
  return null;
};

export const useLinkableContextItems = ({
  query,
  limit = DEFAULT_LIMIT,
  sessions: rawSessions,
  profiles,
  parseTimestamp,
  patientSexAge,
}: UseLinkableContextItemsArgs): LinkableContextItems => {
  const intl = useIntl();
  // Sort newest-first to match the default sessions list sort (createdAt desc).
  // Which page the rows came from is the caller's decision now — a glance surface
  // only ever wanted the first one.
  const sessions = useMemo(() => {
    return [...rawSessions].sort((a, b) => {
      const timeA = parseTimestamp(a.created_time_local ?? a.created_at, !a.ehr_appt_id);
      const timeB = parseTimestamp(b.created_time_local ?? b.created_at, !b.ehr_appt_id);
      return timeB.getTime() - timeA.getTime();
    });
  }, [rawSessions, parseTimestamp]);

  return useMemo(() => {
    const untitledSessionName = intl.formatMessage(sharedAgentMessages.untitledSession);
    const needle = query?.trim().toLowerCase() ?? '';
    const matches = (...fields: Array<string | null | undefined>): boolean =>
      needle === '' || fields.some((f) => f?.toLowerCase().includes(needle));

    // Each session row already carries its linked ``patient_profile``
    // (with the real ``id``) when the session is linked to one — that's
    // the source of truth for the patient artifact. The name-keyed
    // ``profileByName`` below is only a secondary fallback for the rare
    // row whose profile link isn't projected, matching on the same
    // identifier ``getDisplayName`` prefers (``demographic_string``)
    // then the first+last composite.
    const profileByName = new Map<string, string>();
    const profilesById = new Map<string, LinkablePatientProfile>();
    for (const p of profiles ?? []) {
      if (!p.id) continue;
      profilesById.set(p.id, p);
      const identifier = p.demographic_string?.trim();
      const composite = [p.first_name, p.last_name].filter(Boolean).join(' ').trim();
      const key = (identifier || composite).toLowerCase();
      if (!key) continue;
      if (!profileByName.has(key)) profileByName.set(key, p.id);
    }

    const nameFieldsForSession = (
      s: (typeof sessions)[number]
    ): PatientNameFields | undefined => {
      const projected = s.patient_profile;
      if (hasPatientNameFields(projected)) return projected;
      // Read id off the session field, not ``projected``: the name-fields
      // type guard would otherwise narrow the miss branch to ``never``.
      const rosterId =
        s.patient_profile?.id ??
        profileByName.get(s.patient?.name?.trim().toLowerCase() ?? '');
      return rosterId ? profilesById.get(rosterId) : undefined;
    };

    const sessionItems: LinkedContextItem[] = [];
    for (const s of sessions) {
      if (sessionItems.length >= limit) break;
      const sessionName = s.session_name?.trim() || untitledSessionName;
      const profile = nameFieldsForSession(s);
      const patientName = resolvePatientListName(profile, s.patient?.name);
      if (!matches(sessionName, patientName, profile?.last_name, profile?.first_name)) {
        continue;
      }
      sessionItems.push(
        toSessionContextItem(s, profile, {
          sessionName,
          patientName,
          intl,
          parseTimestamp,
        })
      );
    }

    // Only real patient profiles. A session's free-text ``patient.name``
    // (no linked profile, no roster name match) is not a patient — offering
    // it in "Add a patient" links a name the Patients page does not have.
    // Dedupe by that profile id. Attach the id straight off the session's
    // linked ``patient_profile``, falling back to the name match only when
    // the row carries no projected link.
    const seen = new Set<string>();
    const patientItems: LinkedContextItem[] = [];
    for (const s of sessions) {
      const profile = nameFieldsForSession(s);
      const name = resolvePatientListName(profile, s.patient?.name);
      if (!name) continue;
      const embeddedKey = s.patient?.name?.trim().toLowerCase() ?? '';
      const profileId =
        s.patient_profile?.id ??
        profileByName.get(embeddedKey) ??
        profileByName.get(name.toLowerCase());
      if (!profileId) continue;
      if (seen.has(profileId)) continue;
      if (
        !matches(
          name,
          profile?.last_name,
          profile?.first_name,
          profile?.demographic_string
        )
      ) {
        continue;
      }
      if (patientItems.length >= limit) break;
      seen.add(profileId);
      const rosterProfile = profilesById.get(profileId);
      const sexAge = rosterProfile && patientSexAge?.(rosterProfile);
      const lastSeen = objectDayLabel(
        rosterProfile?.last_session_created_at,
        intl,
        parseTimestamp
      );
      patientItems.push({
        type: 'patient',
        patientName: name,
        avatarSeed: resolvePatientAvatarSeed(profile, s.patient?.name),
        patientId: profileId,
        ...(sexAge ? { sexAge } : {}),
        ...(lastSeen ? { lastSeen } : {}),
      });
    }

    return { sessionItems, patientItems };
  }, [sessions, profiles, query, limit, parseTimestamp, intl, patientSexAge]);
};
